/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import { pascalToTitleCase, toSentenceCase, valToBool, isEmptyObject } from "./index";
import { errorToObject, v2 } from "@prftesp/cle-logger";
import errorCodes from "../../../utility/errorCodes";

const COMPONENT_NAME = "AdminCCPUtility/call-events-transformer";
const SET_QUEUE_EVENT = "SetQueue";

/**
 * Transforms CloudWatch contact flow events into user friendly text.
 * Each supported event type will be processed by a corresponding transform function.
 * Each transformation function will get the current event, amazon connect lookup data,
 * and the entire raw events list, and will return a string representing the display text
 * or null. An event is skipped when the event type is not supported, the transformation function
 * returns null, or in case of an unhandled error.
 *
 * @param {Array} rawEvents raw CloudWatch contact events in chronological order
 * @param {Object} lookupData amazon connect lookup data
 * @returns {Array<{id: string, date: Date, type: string, text: text}>} a list of transformed events in reverse chronological order
 */
export const transformEvents = (rawEvents, lookupData) => {
	const events = rawEvents
		.reduce((result, event) => {
			try {
				const type = event.data.ContactFlowModuleType;
				const dataTransformer = _eventTransformers[type];

				if (!dataTransformer) {
					return result;
				}

				const date = new Date(event.timestamp);
				const text = dataTransformer(event, lookupData, rawEvents);

				if (!text) {
					return result;
				}

				result.push({
					id: event.id,
					date,
					type,
					text,
				});
			} catch (error) {
				v2.warn("Failed to transform call/chat history event", {
					componentName: COMPONENT_NAME,
					logCode: errorCodes.UNKNOWN_CONTACT_HISTORY_EVENT_WARNING,
					additionalData: {
						error: errorToObject(error),
						data: {
							type: event?.data?.ContactFlowModuleType,
							time: event?.timestamp,
						},
					},
				});
			}

			return result;
		}, [])
		.reverse();

	return events;
};

/**
 * Private transformation functions for each event. Each function will get the current
 * event, amazon connect lookup data, and the list of all events. The functions should use
 * these parameters to construct an output string representing a user friendly message
 * describing the contact flow event.
 * Some transformation functions will have duplicate logic, this is ok. If it makes sense,
 * extract some common functionality to helper functions at the bottom of this file, but be
 * careful about going too far. Some events may have the same text and conditional logic to
 * construct it, but that is just a coincidence now. As we get feedback from customers these
 * transformations will change a lot, and what looks like duplication now will be useful later.
 * When refactoring, think about what will be beneficial later, rather than what looks perfectly
 * DRY now.
 *
 * The functions should **be pure functions, that have no side effects**, meaning they should:
 * - only depend on their inputs,
 * - not mutate any input parameters
 * - not access or modify any state that is global or outside their scope
 *
 * This makes them 100% independent from the rest of the code, easy to test and understand,
 * easy to modify or add new with confidence that the rest of the code has not accidentally been affected.
 * For more information on pure functions and basic functional programming terms in javascript see
 * https://dev.to/aviaryan/introduction-to-functional-programming-in-javascript-29fp
 */

const _resumeEventTransformer = (event, lookups) => {
	const flow = lookups.contactFlows.find((x) => x.Arn === event.data.ContactFlowId);
	const name = flow ? flow.Name : "N/A";
	return `Initializing end/resume for '${name}'.`;
};

const _setAttributesEventTransformer = (event) => {
	const { Key, Value } = event.data.Parameters;
	return `Setting attribute '${Key}' to '${Value}'.`;
};

const _setQueueEventTransformer = (event, lookups) => {
	const name = _getEventQueueName(event, lookups);
	return `Setting queue to '${name || "N/A"}'.`;
};

const _transferEventTransformer = (event, lookups, events) => {
	const { ContactFlowId, PhoneNumber } = event.data.Parameters;

	if (ContactFlowId) {
		const flow = lookups.contactFlows.find((x) => x.Arn === ContactFlowId || x.Id === ContactFlowId);
		const flowName = flow ? flow.Name : "N/A";

		return `Transferring to '${flowName}' contact flow.`;
	}

	if (PhoneNumber) {
		return `Transferring to ${PhoneNumber}.`;
	}

	const queueEvent = isEmptyObject(event.data.Parameters) ? _findPrevEvent(event, events, SET_QUEUE_EVENT) : event;
	const queueName = _getEventQueueName(queueEvent, lookups);

	return `Queuing to '${queueName}' queue.`;
};

const _checkAttributeEventTransformer = (event) => {
	const { Value, SecondValue, ComparisonMethod } = event.data.Parameters;
	const result = valToBool(event.data.Results);

	return `Check attribute '${Value}' ${pascalToTitleCase(
		ComparisonMethod,
	).toLowerCase()} '${SecondValue}' returned ${result}.`;
};

const _setLoggingBehaviorEventTransformer = (event) => {
	const { LoggingBehavior } = event.data.Parameters;
	return `Logging for this contact is set to '${LoggingBehavior}'.`;
};

const _checkHoursOfOperationEventTransformer = (event, lookups, events) => {
	const { Hours } = event.data.Parameters;
	const { Results } = event.data;

	const prevSetQueueEvent = _findPrevEvent(event, events, SET_QUEUE_EVENT);
	const queueName = _getEventQueueName(prevSetQueueEvent, lookups);

	if (Results === undefined) {
		return `Checking hours of operation for '${queueName}'.`;
	}

	const result = valToBool(event.data.Results);

	if (Hours) {
		const hoursOfOperation = lookups.hoursOfOperation.find((x) => x.Id === Hours);
		return `Contact occurred ${result ? "within" : "outside"} the hours of operation for '${
			hoursOfOperation?.Name || "N/A"
		}'.`;
	}

	return `Contact occurred ${result ? "within" : "outside"} the hours of operation for '${queueName}'.`;
};

const _checkQueueStatusEventTransformer = (event) => {
	const { Value, SecondValue, ComparisonMethod } = event.data.Parameters;
	const hasParams = !isEmptyObject(event.data.Parameters);

	if (!hasParams) {
		return "Checking queue status.";
	}

	return `${toSentenceCase(Value)} ${pascalToTitleCase(ComparisonMethod).toLowerCase()} '${SecondValue}'.`;
};

const _checkStaffingEventTransformer = (event, lookups, events) => {
	const queueEvent = isEmptyObject(event.data.Parameters) ? _findPrevEvent(event, events, SET_QUEUE_EVENT) : event;
	const name = _getEventQueueName(queueEvent, lookups);
	const result = valToBool(event.data.Results);

	return `Check staffing for '${name}' returned ${result}.`;
};

const _invokeExternalResourceEventTransformer = (event) => {
	const { FunctionArn } = event.data.Parameters;
	const successMsg =
		event.data.Results && event.data.Results.toLowerCase().indexOf("error") !== -1
			? "Execution resulted in an error"
			: "Execution was successful";
	return `Executing custom function '${_getResourceNameFromArn(FunctionArn)}'. ${successMsg}.`;
};

const _disconnectEventTransformer = () => {
	return "Disconnecting contact.";
};

const _distributeByPercentageEventTransformer = (event) => {
	const { Allocation } = event.data.Parameters;
	return `Distributing contact based on the ${Allocation[event.data.Results]}% criteria.`;
};

const _getMetricDataEventTransformer = (event, lookups, events) => {
	const queueEvent = isEmptyObject(event.data.Parameters) ? _findPrevEvent(event, events, SET_QUEUE_EVENT) : event;
	const name = _getEventQueueName(queueEvent, lookups);

	return `Getting metrics data for '${name}' queue.`;
};

const _waitEventTransformer = (event) => {
	return event.data.Results === "Error"
		? "Error in waiting event, this event is only supported for chat."
		: `Waiting for a maximum of ${event.data.Parameters.TimeoutSeconds} second(s).`;
};

const _updateAgentStateEventTransformer = (event) => {
	return isEmptyObject(event.data.Parameters)
		? null
		: `Initiating hold style: ${pascalToTitleCase(event.data.Parameters.AgentStatus)}.`;
};

const _setCallBackNumberEventTransformer = (event) => {
	return `Setting callback number to ${event.data.Parameters.PhoneNumberToVerify || "N/A"}.`;
};

const _loopEventTransformer = (event) => {
	return event.data.Results === "NotDone"
		? `Continuing to loop. Loop number: ${event.data.Parameters.LoopCount}.`
		: "Done looping.";
};

const _updateRoutingPriorityEventTransformer = (event) => {
	const { TimeOffset, AbsolutePosition } = event.data.Parameters;
	return AbsolutePosition
		? `Set priority in queue to ${AbsolutePosition}.`
		: `Offset time in the queue by ${TimeOffset} seconds.`;
};

const _startMediaStreamingEventTransformer = (event) => {
	if (isEmptyObject(event.data.Parameters)) {
		return null;
	}

	const { Track } = event.data.Parameters;
	const tracksText = typeof Track === "string" ? pascalToTitleCase(Track) : Track.map(pascalToTitleCase).join(", ");
	return `Starting media streaming for the following tracks: ${tracksText}.`;
};

const _stopMediaStreamingEventTransformer = (event) => {
	if (isEmptyObject(event.data.Parameters)) {
		return null;
	}

	const { Track } = event.data.Parameters;
	const tracksText = typeof Track === "string" ? Track : Track.map(pascalToTitleCase).join(", ");
	return `Stopping media streaming for the following tracks: ${tracksText}.`;
};

const _setContactFlowEventTransformer = (event, lookups) => {
	const { ContactFlowId, Type } = event.data.Parameters;
	const flow = lookups.contactFlows.find((x) => x.Arn === ContactFlowId);
	const flowName = flow ? flow.Name : "N/A";

	return `Setting '${pascalToTitleCase(Type)}' contact flow to '${flowName}'.`;
};

const _setVoiceEventTransformer = (event) => {
	return `Text-to-speech voice set to ${event.data.Parameters.GlobalVoice}.`;
};

const _setRecordingBehaviorEventTransformer = (event) => {
	return `Recording for this contact is set to '${event.data.Parameters.RecordingBehaviorOption}'.`;
};

const _playPromptEventTransformer = (event, lookups) => {
	const { Text, TextToSpeechType, AudioPrompt } = event.data.Parameters;

	if (TextToSpeechType === "ssml") {
		return "Playing SSML message.";
	}

	if (Text) {
		return "Delivering TTS message.";
	}

	const prompt = lookups.prompts.find((x) => x.Arn === AudioPrompt);
	const promptName = prompt ? prompt.Name : "N/A";

	return `Playing prompt: '${promptName}'.`;
};

const _loopPromptsEventTransformer = (event, lookups) => {
	if (isEmptyObject(event.data.Parameters)) {
		return null;
	}

	const { InterruptSeconds, AudioPrompt, Text, TextToSpeechType } = event.data.Parameters;

	if (InterruptSeconds) {
		return `Interrupting looping prompt after ${InterruptSeconds} seconds.`;
	}

	if (AudioPrompt) {
		const prompt = lookups.prompts.find((x) => x.Arn === AudioPrompt);
		const promptName = prompt ? prompt.Name : "N/A";

		return `Playing prompt: '${promptName}'.`;
	}

	if (Text) {
		return TextToSpeechType === "ssml" ? "Playing SSML message." : "Delivering TTS message.";
	}
};

const _storeUserInputEventTransformer = (event, lookups) => {
	if (isEmptyObject(event.data.Parameters)) {
		return null;
	}

	const { InterruptSeconds, AudioPrompt, Text, TextToSpeechType } = event.data.Parameters;

	if (InterruptSeconds) {
		return `Interrupting looping prompt after ${InterruptSeconds} seconds.`;
	}

	if (AudioPrompt) {
		const prompt = lookups.prompts.find((x) => x.Arn === AudioPrompt);
		const promptName = prompt ? prompt.Name : "N/A";

		return `Playing prompt: '${promptName}'.`;
	}

	if (Text) {
		return TextToSpeechType === "ssml" ? "Playing SSML message." : "Delivering TTS message.";
	}
};

const _getUserInputEventTransformer = (event, lookups) => {
	const results = event.data.Results;

	if (results === "Timeout") {
		return "Waiting for customer input timed out.";
	}

	if (results) {
		return `Collecting customer input: '${results}'.`;
	}

	const { Text, TextToSpeechType, AudioPrompt } = event.data.Parameters;

	if (TextToSpeechType === "ssml") {
		return "Playing SSML message.";
	}

	if (Text) {
		return "Delivering TTS message.";
	}

	const prompt = lookups.prompts.find((x) => x.Arn === AudioPrompt);
	const promptName = prompt ? prompt.Name : "N/A";

	return `Playing prompt: '${promptName}'.`;
};

const _dialEventTransformer = (event) => {
	const successMsg =
		event.data.Results === "Success" ? "Call was completed successfully" : "Call could not be completed as dialed";

	return `Dialing out to ${event.data.Parameters.CallerId}. ${successMsg}.`;
};

/**
 * A map of all transformation functions to call flow events.
 * To support additional events, add an entry here for your transformation
 * functions, where the key is the 'ContactFlowModuleType' property on
 * the CloudWatch contact flow log object.
 */

const _eventTransformers = {
	CheckAttribute: _checkAttributeEventTransformer,
	CheckHoursOfOperation: _checkHoursOfOperationEventTransformer,
	CheckQueueStatus: _checkQueueStatusEventTransformer,
	CheckStaffing: _checkStaffingEventTransformer,
	Dial: _dialEventTransformer,
	Disconnect: _disconnectEventTransformer,
	DistributeByPercentage: _distributeByPercentageEventTransformer,
	GetMetricData: _getMetricDataEventTransformer,
	GetUserInput: _getUserInputEventTransformer,
	InvokeExternalResource: _invokeExternalResourceEventTransformer,
	Loop: _loopEventTransformer,
	LoopPrompts: _loopPromptsEventTransformer,
	PlayPrompt: _playPromptEventTransformer,
	Resume: _resumeEventTransformer,
	SetAttributes: _setAttributesEventTransformer,
	SetCallBackNumber: _setCallBackNumberEventTransformer,
	SetContactFlow: _setContactFlowEventTransformer,
	SetLoggingBehavior: _setLoggingBehaviorEventTransformer,
	SetQueue: _setQueueEventTransformer,
	SetRecordingBehavior: _setRecordingBehaviorEventTransformer,
	SetVoice: _setVoiceEventTransformer,
	StartMediaStreaming: _startMediaStreamingEventTransformer,
	StopMediaStreaming: _stopMediaStreamingEventTransformer,
	StoreUserInput: _storeUserInputEventTransformer,
	Transfer: _transferEventTransformer,
	UpdateAgentState: _updateAgentStateEventTransformer,
	UpdateRoutingPriority: _updateRoutingPriorityEventTransformer,
	Wait: _waitEventTransformer,
};

/**
 * Private helper functions
 */

/**
 * Finds the first event of a given type, that occurred before the current event
 * @param {Object} currentEvent
 * @param {Array} events
 * @param {String} eventTypeToFind
 */
const _findPrevEvent = (currentEvent, events, eventTypeToFind) => {
	const currentEventIndex = events.findIndex((x) => x.id === currentEvent.id);

	if (currentEventIndex < 0) {
		return null;
	}

	const eventsBeforeCurrent = events.slice(0, currentEventIndex + 1);

	for (let i = eventsBeforeCurrent.length - 1; i >= 0; i--) {
		if (eventsBeforeCurrent[i].data.ContactFlowModuleType === eventTypeToFind) {
			return eventsBeforeCurrent[i];
		}
	}

	return null;
};

/**
 * Gets the name of the queue or agent from an event
 * @param {Object} event
 * @param {Object} lookups
 */
const _getEventQueueName = (event, lookups) => {
	if (!event) {
		return "N/A";
	}

	let name = "";

	if (event.data.Parameters.Queue) {
		const queue = lookups.queues.find((x) => x.Arn === event.data.Parameters.Queue);
		name = queue?.Name;
	} else if (event.data.Parameters.Agent) {
		const agent = lookups.users.find((x) => x.Arn === event.data.Parameters.Agent);
		name = agent?.Username;
	}

	return name || "N/A";
};

/**
 * Gets the substring of an ARN after the last ':'
 * @param {String} arn
 */
const _getResourceNameFromArn = (arn) => arn.substring(arn.lastIndexOf(":") + 1);
